// Motor insurance journey (Orient via the Al Futtaim gateway today; the
// config names no carrier, so a second motor carrier changes nothing here).

/** @type {import("../../form/types").JourneyConfig} */
const motor = {
  key: "motor",
  label: "Motor Insurance",

  // The exact value redux stores in `insuranceType`; the route guard
  // restores it from here after a refresh or a deep link.
  insuranceType: "Motor Insurance",
  entry: "/motor-landing",
  landing: "landing:motor",
  caseIdPrefix: "M",

  rail: {
    greeting: "Hi there",
    image: "/assets/images/cartoon-car.png",
    bullets: [
      "Instant policies online",
      "Flexible payment options",
      "Insurers you can trust",
      "24/7 claims support",
    ],
  },

  steps: [
    {
      key: "type",
      summary: ["insured_type"],
      label: "Insurance type",
      path: "/motor-insurance-type",
      mobileTitle: "Insurance type",
      rail: { title: "who is this", accent: "policy for?" },
      hideCta: true,
      form: {
        id: "motor-type",
        sections: [
          {
            fields: [
              {
                name: "insured_type",
                type: "choice",
                label: "Who is the policy for?",
                required: true,
                col: 12,
                props: { illustrated: true },
                options: {
                  source: "reference",
                  domain: "insured-type",
                  decorate: {
                    INDIVIDUAL: {
                      label: "Individual",
                      subtitle: "Personal policy",
                      image: "/assets/images/motor-individual.png",
                      badge: "Most common",
                      perks: [
                        "Policy issued in your own name",
                        "Emirates ID & driving licence based",
                        "Fast online issuance",
                      ],
                    },
                    CORPORATE: {
                      label: "Corporate",
                      subtitle: "Company policy",
                      image: "/assets/images/motor-corporate.png",
                      badge: "Fleet-ready",
                      perks: [
                        "Issued to your company",
                        "Trade licence based",
                        "Multiple vehicles supported",
                      ],
                    },
                  },
                },
              },
            ],
          },
        ],
      },
    },

    {
      key: "documents",
      label: "Documents",
      path: "/document-upload",
      screen: "document-upload",
      mobileTitle: "Your documents",
      rail: {
        title: "upload your",
        accent: "documents",
        subtitle:
          "All optional — we read what you upload.",
      },
      cta: "Continue to your vehicle",
    },

    {
      key: "vehicle",
      summary: ["veh_make_code", "make_year"],
      label: "Your vehicle",
      path: "/motor-vehicle",
      mobileTitle: "Your vehicle",
      rail: {
        title: "tell us about your",
        accent: "car",
        subtitle: "Make, model and year set the base premium for every quote.",
      },
      cta: "Continue to owner details",
      form: {
        id: "motor-vehicle",
        sections: [
          {
            fields: [
              {
                name: "veh_make_code",
                type: "select",
                label: "Make",
                required: true,
                options: { source: "reference", domain: "vehicle-make" },
                // The mulkiya states the make and model ("CHEVROLET TAHOE") in
                // its Vehicle Type line, and we were reading that document for
                // the chassis number anyway while leaving these two to be
                // chosen by hand from a list hundreds long. Orient checks the
                // pair against Bayanati at issuance, so a mis-pick is only
                // discovered at the very last step.
                //
                // No transform: SelectInput resolves free text to a code once
                // the options load, and clears it when there is no confident
                // match, so a line it cannot read leaves the field blank rather
                // than guessing.
                ocrKeys: "vehicle.Vehicle_Type",
              },
              {
                name: "veh_model_code",
                type: "select",
                label: "Model",
                required: true,
                ocrKeys: "vehicle.Vehicle_Type",
                options: {
                  source: "reference",
                  domain: "vehicle-model",
                  dependsOn: "veh_make_code",
                },
                props: {
                  // Orient lists no models at all for some makes. Without this
                  // the dropdown just opened onto nothing.
                  emptyText: "No models listed for this make",
                  emptyHint:
                    "We can't quote this make yet — please choose a different one.",
                },
              },
              {
                name: "make_year",
                type: "select",
                label: "Year of manufacture",
                required: true,
                options: { source: "reference", domain: "manufacture-year" },
                ocrKeys: "vehicle.Year_of_Manufacture",
                ocrTransform: "year",
                // Orient refuses a gap of more than 2 years against the first
                // registration date (on the car-details step). Caught here so
                // the mismatch shows on the field instead of surfacing as a
                // dead end once the user reaches the plans screen.
                rules: [
                  {
                    rule: "withinYearsOf",
                    field: "date_of_first_regn",
                    max: 2,
                    message:
                      "This should be within 2 years of the first registration date on the car details step",
                  },
                ],
              },
            ],
          },
        ],
      },
    },

    {
      key: "owner",
      summary: ["full_name", "nationality"],
      label: "Owner details",
      path: "/motor-owner-details",
      mobileTitle: "Owner details",
      rail: {
        title: "tell us about the",
        accent: "owner",
        subtitle: "We've pre-filled what we could read from your documents.",
      },
      cta: "Continue to car details",
      form: {
        id: "motor-owner",
        sections: [
          {
            fields: [
              {
                name: "full_name",
                type: "text",
                label: "Full name",
                help: "Exactly as printed on your Emirates ID",
                required: true,
                ocrKeys: "Name",
              },
              {
                name: "emirates_id",
                type: "text",
                label: "Emirates ID",
                help: "15 digits from the front of the card",
                placeholder: "784-YYYY-XXXXXXX-X",
                required: true,
                rules: ["emiratesId"],
                ocrKeys: "ID_Number",
              },
              {
                name: "emirates_id_expiry",
                type: "date",
                label: "Emirates ID expiry",
                required: true,
                rules: ["notExpired"],
                ocrKeys: "Expiry_Date",
                props: { minDate: "today" },
              },
              {
                name: "nationality",
                type: "select",
                label: "Nationality",
                required: true,
                options: { source: "reference", domain: "nationality" },
                ocrKeys: "Nationality",
              },
              {
                name: "driver_dob",
                type: "date",
                label: "Date of birth",
                required: true,
                rules: [{ rule: "age", min: 18, message: "The policyholder must be 18 or older" }],
                ocrKeys: "Date_of_Birth",
                props: { maxDate: "today" },
              },
              {
                name: "gender",
                type: "select",
                label: "Gender",
                required: true,
                options: { source: "reference", domain: "gender" },
                ocrKeys: "Sex",
              },
              {
                name: "mobile",
                type: "phone",
                label: "Mobile number",
                required: true,
                rules: ["uaeMobile"],
              },
              {
                name: "email",
                type: "email",
                label: "Email",
                help: "Your quote and policy documents are sent here",
                required: true,
                rules: ["email"],
                placeholder: "name@example.com",
              },
            ],
          },
          {
            title: "Address",
            fields: [
              { name: "po_box_no", type: "text", label: "P.O. Box", help: "Leave blank if you don't have one" },
              { name: "address", type: "text", label: "Address", help: "Where you live — building, street and area" },
            ],
          },
          {
            title: "Occupation",
            description:
              "The insurer prices partly on what you do, so this one is required.",
            visibleWhen: { field: "insured_type", equals: "INDIVIDUAL" },
            fields: [
              {
                name: "profession",
                type: "select",
                label: "Profession",
                col: 6,
                // Orient's createPolicyHeader has one field for this and picks
                // the code list by insured type: PROFESSSION for a person,
                // BUS_ACTIVITY for a company. Sending neither is rejected
                // outright, which is why an individual quotation used to fail.
                requiredWhen: { field: "insured_type", equals: "INDIVIDUAL" },
                options: { source: "reference", domain: "profession" },
              },
            ],
          },
          {
            title: "Company details",
            description: "Required for corporate policies.",
            visibleWhen: { field: "insured_type", equals: "CORPORATE" },
            fields: [
              {
                name: "trade_license",
                type: "text",
                label: "Trade licence no.",
                help: "As printed on the company's trade licence",
                requiredWhen: { field: "insured_type", equals: "CORPORATE" },
              },
              {
                name: "commercial_reg_no",
                type: "text",
                label: "Commercial reg. no.",
                help: "The CR number on the same licence",
                requiredWhen: { field: "insured_type", equals: "CORPORATE" },
              },
              {
                // Neither of these was ever collected, so saveAdditionalDetails
                // sent both blank on every corporate case, and the quote itself
                // was refused outright with `06 tradeLicDate Invalid Value` —
                // the expiry is what the header carries.
                name: "trade_lic_iss_date",
                type: "date",
                label: "Trade licence issued",
                requiredWhen: { field: "insured_type", equals: "CORPORATE" },
                props: { maxDate: "today" },
              },
              {
                name: "trade_lic_exp_date",
                type: "date",
                label: "Trade licence expiry",
                requiredWhen: { field: "insured_type", equals: "CORPORATE" },
                rules: ["notExpired"],
              },
            ],
          },
        ],
      },
    },

    {
      key: "car",
      summary: ["veh_reg_no", "car_value"],
      label: "Car details",
      path: "/motor-car-details",
      mobileTitle: "Car details",
      rail: {
        title: "a few details about your",
        accent: "car",
        subtitle: "These come off your Car Registration Certificate (Mulkiya) — we've read what we could.",
      },
      cta: "See your plans",
      form: {
        id: "motor-car",
        sections: [
          {
            title: "Vehicle specification",
            fields: [
              { name: "chassis_no", type: "text", label: "Chassis number", required: true, ocrKeys: "vehicle.Chassis_No", help: "17 characters, shown on the Car Registration Certificate (Mulkiya) as Chassis No." },
              { name: "engine_no", type: "text", label: "Engine number", ocrKeys: "vehicle.Engine_No", help: "On the Car Registration Certificate (Mulkiya), under Engine No." },
              { name: "engine_capacity", type: "number", label: "Engine capacity (cc)", help: "In cc — a 2.0 litre engine is 2000" },
              {
                name: "cylinder_capacity",
                type: "select",
                label: "Cylinders",
                options: { source: "reference", domain: "cylinder" },
              },
              { name: "loading_capacity", type: "number", label: "Loading capacity", help: "Pickups and trucks only — leave blank for a car" },
              {
                name: "date_of_first_regn",
                type: "date",
                label: "First registration date",
                required: true,
                ocrKeys: "vehicle.Registration_Date",
                props: { maxDate: "today" },
                // Mirror of the `make_year` rule, so whichever of the two the
                // user edits last is the one that reports the mismatch.
                rules: [
                  {
                    rule: "withinYearsOf",
                    field: "make_year",
                    max: 2,
                    message:
                      "This should be within 2 years of the year of manufacture on the vehicle step",
                  },
                ],
              },
              { name: "veh_mileage", type: "number", label: "Mileage (km)", help: "Roughly what the odometer reads today" },
              {
                name: "number_of_passengers",
                type: "number",
                label: "Passenger capacity",
                help: "Including the driver, as shown on the Car Registration Certificate (Mulkiya)",
                ocrKeys: "vehicle.Number_of_Passengers",
                ocrTransform: "digits",
              },
              {
                name: "veh_color",
                type: "select",
                label: "Colour",
                options: { source: "reference", domain: "vehicle-color" },
              },
              {
                // Mandatory on Orient's saveAdditionalDetails and never asked
                // for, so that call could not succeed. The mulkiya states it on
                // its Origin line, so most customers never have to answer it.
                name: "country_of_manf",
                type: "select",
                label: "Country of manufacture",
                required: true,
                options: { source: "reference", domain: "country-of-make" },
                ocrKeys: "vehicle.Origin",
              },
              {
                name: "vehicle_usage",
                type: "select",
                label: "Usage",
                required: true,
                options: { source: "reference", domain: "vehicle-usage" },
              },
              {
                name: "geographical_area",
                type: "select",
                label: "Geographic area",
                options: { source: "reference", domain: "geographic-area" },
              },
              {
                name: "business_activity",
                type: "select",
                label: "Business activity",
                visibleWhen: { field: "insured_type", equals: "CORPORATE" },
                options: { source: "reference", domain: "business-activity" },
              },
            ],
          },
          {
            title: "Registration",
            fields: [
              // Optional: a car straight from the showroom has no plate yet.
              // Orient still lists vehRegNo as mandatory on
              // saveAdditionalDetails (docs/MOTOR-API.md), so a blank one can
              // be refused at issuance rather than at quoting.
              { name: "veh_reg_no", type: "text", label: "Plate number", ocrKeys: "vehicle.Traffic_Plate_No", help: "Just the digits — leave blank if the car has no plate yet" },
              {
                name: "veh_plate_code",
                type: "select",
                label: "Plate code",
                options: { source: "reference", domain: "plate-code" },
              },
              {
                name: "veh_plate_category",
                type: "select",
                label: "Plate category",
                options: { source: "reference", domain: "plate-category" },
              },
              {
                name: "veh_reg_emirate",
                type: "select",
                label: "Registration emirate",
                required: true,
                options: { source: "reference", domain: "emirate" },
                ocrKeys: "vehicle.Place_of_Issue",
              },
              {
                name: "veh_type_of_reg",
                type: "select",
                label: "Registration type",
                options: {
                  source: "reference",
                  domain: "registration-type",
                  dependsOn: "insured_type",
                },
              },
            ],
          },
          {
            title: "Cover and history",
            fields: [
              {
                name: "car_value",
                type: "currency",
                label: "Vehicle value",
                required: true,
                help: "The sum insured your comprehensive cover is based on",
                rules: [{ rule: "range", min: 1000 }],
              },
              {
                name: "current_insr_exp_dt",
                type: "date",
                label: "Current policy expiry",
                help: "Leave blank if this is a brand-new vehicle",
                ocrKeys: "vehicle.Insurance_Expiry",
              },
              {
                // Was hard-coded to "N" in the payload — we told the insurer the
                // car was not brand new without ever asking. Harmless for a used
                // car; wrong, and a misdeclaration, for one bought from a
                // showroom.
                name: "brand_new",
                type: "radio",
                label: "Is this a brand-new vehicle?",
                required: true,
                defaultValue: "N",
                options: [
                  { value: "Y", label: "Yes" },
                  { value: "N", label: "No" },
                ],
              },
              {
                name: "prev_owner",
                type: "radio",
                label: "Has the car had a previous owner?",
                defaultValue: "N",
                options: [
                  { value: "Y", label: "Yes" },
                  { value: "N", label: "No" },
                ],
              },
              {
                name: "prev_pol_type",
                type: "select",
                label: "Previous policy type",
                options: { source: "reference", domain: "previous-policy-type" },
              },
              {
                name: "agency_repair_yn",
                type: "radio",
                label: "Do you want agency repair?",
                defaultValue: "N",
                help: "Repairs at the manufacturer's workshop, at a higher premium",
                options: [
                  { value: "Y", label: "Yes" },
                  { value: "N", label: "No" },
                ],
              },
              {
                name: "uae_driving_exp",
                type: "select",
                label: "UAE driving experience",
                required: true,
                options: { source: "reference", domain: "driving-experience-uae" },
              },
              {
                name: "home_driving_exp",
                type: "select",
                label: "Home country driving experience",
                options: { source: "reference", domain: "driving-experience-home" },
              },
              {
                name: "no_claim_certificate",
                type: "select",
                label: "No-claim years",
                help: "A no-claims certificate from your previous insurer earns a discount",
                options: { source: "reference", domain: "no-claim-years" },
              },
            ],
          },
        ],
      },
    },

    {
      key: "choose-plan",
      label: "Choose plan",
      path: "/motor-quote-details",
      screen: "motor-plans",
      aliasPaths: [{ path: "/motor-quote-compare", screen: "motor-compare" }],
      layout: "wide",
      mobileTitle: "Choose your plan",
      rail: { title: "choose your", accent: "plan" },
      hideCta: true,
    },

    {
      key: "policy",
      summary: ["driver_name", "driver_lic_no"],
      label: "Policy details",
      path: "/motor-policy-details",
      mobileTitle: "Policy details",
      rail: {
        title: "confirm your",
        accent: "policy details",
        subtitle: "The licence details of whoever will be driving the car.",
      },
      cta: "Review and pay",
      form: {
        id: "motor-policy",
        sections: [
          {
            title: "Driving licence",
            fields: [
              { name: "driver_name", type: "text", label: "Driver name", required: true, ocrKeys: "license.Name,Name", help: "The main driver, as printed on their licence" },
              { name: "driver_lic_no", type: "text", label: "Licence number", required: true, ocrKeys: "license.License_No", help: "On the front of the UAE driving licence" },
              {
                name: "driver_lic_emirate",
                type: "select",
                label: "Place of issue",
                required: true,
                options: { source: "reference", domain: "emirate" },
                ocrKeys: "license.Place_of_Issue",
              },
              {
                name: "driver_lic_iss_date",
                type: "date",
                label: "Issue date",
                required: true,
                ocrKeys: "license.Issue_Date",
                props: { maxDate: "today" },
              },
              {
                name: "driver_lic_exp_date",
                type: "date",
                label: "Expiry date",
                required: true,
                rules: ["notExpired"],
                ocrKeys: "license.Expiry_Date",
                props: { minDate: "today" },
              },
            ],
          },
        ],
      },
    },

    {
      key: "review-pay",
      label: "Review & pay",
      path: "/motor-plan-summary",
      screen: "motor-summary",
      layout: "wide",
      mobileTitle: "Review & pay",
      rail: { title: "one last", accent: "step" },
      cta: "Confirm & issue policy",
    },
  ],
};

export default motor;
